import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmSignIn, signIn } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '@/services/apiGateway';
import { useAuthStore } from '@/store/authStore';

export function ChildLoginPage() {
  const { t } = useTranslation();
  const [digits, setDigits] = useState<string[]>(Array(8).fill(''));
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setHasError(false);
    if (digit && index < 7) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const joinCode = digits.join('');
    if (joinCode.length < 8) return;

    setIsLoading(true);
    setHasError(false);
    try {
      // Step 1: resolve child_id from the join code
      const { child_id } = await apiFetch<{ child_id: string }>('/auth/resolve-code', {
        method: 'POST',
        body: JSON.stringify({ join_code: joinCode }),
      });

      // Step 2: start Cognito custom auth — no password, challenge only
      const signInResult = await signIn({
        username: `child-${child_id}@studywize.local`,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      });

      // Step 3: respond to the custom challenge with the raw join code
      // The Cognito VerifyAuthChallenge trigger hashes it and checks DynamoDB
      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        await confirmSignIn({ challengeResponse: joinCode });
      }

      // Step 4: hydrate Zustand from the Cognito session that was just issued
      await useAuthStore.getState().init();
      navigate('/student');
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <div
          className="app-content"
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            textAlign: 'center',
            backgroundColor: 'white',
          }}
        >
          <h1 style={{ color: 'var(--primary)', marginTop: 0, marginBottom: '32px', fontSize: '2rem', fontWeight: 700 }}>
            VADOStudy
          </h1>

          <h2 style={{ color: 'var(--text-dark)', marginTop: 0, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 700 }}>
            {t('auth.child_welcome')}
          </h2>

          <p style={{ color: 'var(--text-muted)', margin: '0 0 32px', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {t('auth.child_instructions')}
          </p>

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>

            {/* OTP inputs — two groups of 4 separated by a dash */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginBottom: '24px' }}>
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="tel"
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  className="otp-input"
                  value={digits[i]}
                  onChange={(e) => handleChange(i, e)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={isLoading}
                  style={{ color: '#1f2937', background: '#ffffff', caretColor: 'var(--primary)' }}
                />
              ))}

              <span style={{ fontWeight: 700, fontSize: '1.5rem', color: 'var(--text-muted)' }}>-</span>

              {[4, 5, 6, 7].map((i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="tel"
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  className="otp-input"
                  value={digits[i]}
                  onChange={(e) => handleChange(i, e)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={isLoading}
                  style={{ color: '#1f2937', background: '#ffffff', caretColor: 'var(--primary)' }}
                />
              ))}
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={isLoading || digits.join('').length < 8}>
              {isLoading ? t('auth.verifying') : t('auth.sign_in_btn')}
            </button>

            {hasError && (
              <p style={{
                color: 'var(--danger)',
                fontSize: '0.85rem',
                margin: '16px 0 0',
                padding: '10px 14px',
                background: '#FEF2F2',
                borderRadius: '8px',
                lineHeight: 1.5,
              }}>
                ⚠️ {t('auth.join_code_invalid')}
              </p>
            )}

          </form>
        </div>
      </div>
    </div>
  );
}
